import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Check,
  ArrowRight,
  CreditCard,
  Wallet,
  Building2,
  Lock,
  ShieldCheck,
  Sparkles,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { useToast } from "../hooks/use-toast";
import { PACKAGES, PAYMENT_METHODS, CONTACT } from "../data/mock";
import { PROMO } from "../data/promo";
import CertificazioniModal, { PACCHETTI_CORSO } from "./CertificazioniModal";
import CheckoutModal from "./CheckoutModal";

const API = process.env.REACT_APP_BACKEND_URL;
const BANK = CONTACT.bank;

// Mappa pacchetti frontend → backend
const PKG_BACKEND_ID = {
  "tessera": "tessera",
  "tessera-corso": "tessera-corso",
  "premium": "premium",
};

function formatPrice(v) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(v);
}

function pkgHasCorso(p) {
  return PROMO.active && (p.items || []).some(
    (it) => /Corso A.C.D.T. per la qualificazione professionale/i.test(it.label || "") && Number(it.price) === 99
  );
}

function pkgListinoTotale(p) {
  return p.total + PROMO.scontoEuro;
}

function PayPalMark() {
  return (
    <span className="inline-flex items-center gap-0.5 text-[15px] font-extrabold">
      <span className="text-[#003087]">Pay</span>
      <span className="text-[#009cde]">Pal</span>
    </span>
  );
}
function KlarnaMark() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#FFA8CD] text-[#17120F] text-[12px] font-extrabold tracking-wide">
      Klarna
    </span>
  );
}
function StripeMark() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#635BFF] text-white text-[12px] font-extrabold tracking-wide">
      stripe
    </span>
  );
}
function GooglePayMark() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-slate-300 text-[12px] font-bold">
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.97 10.97 0 0 0 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      <span className="text-slate-700">G Pay</span>
    </span>
  );
}
function ApplePayMark() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-black text-white text-[12px] font-bold">
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.5 12.5c0-2.5 2-3.7 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.8-3.2-.8-1.6 0-3.2 1-4 2.5-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.8 1.3-2.9-.1 0-2.5-1-2.5-3.9zM14.9 4.7c.7-.8 1.1-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3.1 1.1.1 2.2-.6 2.9-1.4z"/>
      </svg>
      <span>Pay</span>
    </span>
  );
}

const METHOD_ICON = {
  paypal: PayPalMark,
  googlepay: GooglePayMark,
  applepay: ApplePayMark,
  klarna: KlarnaMark,
  bonifico: () => <Building2 className="h-4 w-4 text-[#102a6b]" />,
};

export default function Packages() {
  const [selectedId, setSelectedId] = useState("tessera-corso");
  const [checkoutPkg, setCheckoutPkg] = useState(null); // pacchetto in checkout (apre modal)
  const [submitting, setSubmitting] = useState(false);
  const [certModal, setCertModal] = useState(null); // {billing, package_id} in attesa di conferma cert
  const { toast } = useToast();

  const selected = useMemo(
    () => PACKAGES.find((p) => p.id === selectedId) || PACKAGES[0],
    [selectedId]
  );

  const openCheckout = (pkg) => {
    setSelectedId(pkg.id);
    setCheckoutPkg(pkg);
  };

  const handleModalSubmit = async (billing) => {
    const package_id = PKG_BACKEND_ID[checkoutPkg.id];
    if (!package_id) {
      toast({ title: "Pacchetto non disponibile online", description: "Contatta la segreteria." });
      return;
    }
    // Se è un pacchetto corso → mostra prima il popup certificazioni sopra al CheckoutModal.
    if (PACCHETTI_CORSO.includes(package_id)) {
      setCertModal({ billing, package_id });
      return;
    }
    await doCheckout({ package_id, billing, cert_l4: false, cert_cfu: false });
  };

  const doCheckout = async ({ package_id, billing, cert_l4, cert_cfu }) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/checkout/create-session`, {
        package_id,
        billing,
        origin_url: window.location.origin,
        certificazione_l4: !!cert_l4,
        certificazione_cfu: !!cert_cfu,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      setSubmitting(false);
      toast({
        title: "Errore avvio pagamento",
        description: err.response?.data?.detail || err.message || "Riprova",
      });
    }
  };

  return (
    <section
      id="pacchetti"
      className="relative py-24 md:py-32 bg-gradient-to-b from-white via-[#f5f7ff] to-white"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto reveal">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
            Pacchetti & Iscrizione
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-[#0a1f55] leading-[1.05]">
            Scegli il pacchetto più adatto a te
          </h2>
          <p className="mt-4 text-slate-600">
            Seleziona la soluzione, completa i dati e paga in modo sicuro con PayPal, Carta
            (Stripe), Klarna o Bonifico bancario.
          </p>
        </div>

        {/* Packages row */}
        <div className="mt-12 grid md:grid-cols-3 gap-5 items-stretch">
          {PACKAGES.map((p) => {
            const isSelected = p.id === selectedId;
            const hasPromo = pkgHasCorso(p);
            const listinoTot = hasPromo ? pkgListinoTotale(p) : null;
            return (
              <button
                type="button"
                key={p.id}
                data-testid={`home-pkg-${p.id}`}
                onClick={() => openCheckout(p)}
                className={`text-left relative rounded-2xl border bg-white p-7 lift transition-all duration-300 ${
                  isSelected
                    ? "border-[#102a6b] shadow-[0_24px_70px_-25px_rgba(16,42,107,0.45)] ring-2 ring-[#102a6b]/15"
                    : "border-slate-200 hover:border-[#102a6b]/50"
                }`}
              >
                {p.badge && (
                  <div
                    className={`absolute -top-3 left-6 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                      p.accent
                        ? "bg-[#f59e0b] text-[#0a1f55]"
                        : "bg-[#102a6b] text-white"
                    }`}
                  >
                    {p.badge}
                  </div>
                )}
                {hasPromo && (
                  <div className="absolute -top-3 right-6 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-red-600 text-white shadow-md inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> -{PROMO.scontoEuro}€
                  </div>
                )}

                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-extrabold text-[#0a1f55]">
                      {p.name}
                    </h3>
                    <p className="mt-1 text-[13px] text-slate-500">{p.tagline}</p>
                  </div>
                  <div
                    className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isSelected
                        ? "bg-[#102a6b] border-[#102a6b]"
                        : "border-slate-300"
                    }`}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                  </div>
                </div>

                <div className="mt-5 flex items-baseline gap-2 flex-wrap">
                  {hasPromo && (
                    <span className="text-lg font-bold text-slate-400 line-through">
                      {formatPrice(listinoTot)}
                    </span>
                  )}
                  <span className="text-4xl font-extrabold text-[#102a6b]">
                    {formatPrice(p.total)}
                  </span>
                  <span className="text-xs text-slate-500">/ una tantum</span>
                </div>
                {hasPromo && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-red-600 uppercase tracking-wider">
                    <Sparkles className="h-3 w-3" /> {PROMO.labelOfferta}
                  </div>
                )}

                <div className="mt-5 space-y-2 border-t border-dashed border-slate-200 pt-4">
                  {p.items.map((it) => {
                    const corsoLine = PROMO.active && /Corso A.C.D.T. per la qualificazione professionale/i.test(it.label || "") && Number(it.price) === 99;
                    return (
                      <div
                        key={it.label}
                        className="flex items-start justify-between gap-3 text-[13px]"
                      >
                        <span className="text-slate-700">{it.label}</span>
                        <span className="font-semibold text-[#0a1f55] whitespace-nowrap">
                          {it.free ? (
                            <span className="text-[#f59e0b]">Incluso</span>
                          ) : corsoLine ? (
                            <span className="inline-flex items-baseline gap-1">
                              <span className="line-through text-slate-400 text-[12px]">{formatPrice(PROMO.prezzoListinoCorso)}</span>
                              <span className="text-red-600">{formatPrice(it.price)}</span>
                            </span>
                          ) : (
                            formatPrice(it.price)
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <ul className="mt-5 space-y-2.5">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-[13.5px] text-slate-700"
                    >
                      <Check className="h-4 w-4 mt-0.5 text-[#f59e0b] shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div
                  className={`mt-6 h-10 rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
                    isSelected
                      ? "bg-[#f59e0b] text-[#0a1f55]"
                      : "bg-[#102a6b] text-white"
                  }`}
                >
                  <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                  {isSelected ? "Acquista ora" : "Scegli e paga"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Modal Checkout */}
      {checkoutPkg && (
        <CheckoutModal
          pacchetto={{
            id: checkoutPkg.id,
            name: checkoutPkg.name,
            price: checkoutPkg.total,
            items: checkoutPkg.items,
          }}
          onSubmit={handleModalSubmit}
          onClose={() => setCheckoutPkg(null)}
          submitting={submitting}
        />
      )}

      {certModal && (
        <CertificazioniModal
          prezzoBase={selected.total}
          packageName={selected.name}
          onClose={() => setCertModal(null)}
          onConfirm={async ({ cert_l4, cert_cfu }) => {
            const p = certModal;
            setCertModal(null);
            await doCheckout({ package_id: p.package_id, billing: p.billing, cert_l4, cert_cfu });
          }}
        />
      )}
    </section>
  );
}


function Field({ id, label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[13px] font-semibold text-[#0a1f55]">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        className="mt-1.5 h-11 border-slate-300 focus-visible:ring-[#102a6b]/40"
        placeholder={label.replace(" *", "")}
      />
    </div>
  );
}

function MethodDetails({ method, causale }) {
  if (method === "paypal") {
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#003087]/10 text-[#003087] text-[10.5px] font-extrabold uppercase tracking-wider">
            Rateizzabile
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[10.5px] font-extrabold uppercase tracking-wider">
            3 · 6 · 12 rate
          </span>
        </div>
        <p className="text-[13.5px] text-slate-600">
          Verrai reindirizzato su <strong>PayPal</strong> per completare il pagamento.
          Puoi pagare in un&apos;unica soluzione oppure scegliere <strong>PayPal Paga in 3, 6 o 12 rate</strong>
          (subordinato all&apos;approvazione PayPal e alla disponibilità del servizio).
        </p>
      </div>
    );
  }
  if (method === "googlepay") {
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <p className="text-[13.5px] text-slate-600">
          Verrai reindirizzato per autorizzare il pagamento tramite <strong>Google Pay</strong> con
          il tuo account Google e i metodi di pagamento associati.
        </p>
      </div>
    );
  }
  if (method === "applepay") {
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <p className="text-[13.5px] text-slate-600">
          Conferma il pagamento con <strong>Apple Pay</strong> tramite Touch ID, Face ID o passcode
          sul tuo dispositivo Apple.
        </p>
      </div>
    );
  }
  if (method === "klarna") {
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FFA8CD] text-[#17120F] text-[10.5px] font-extrabold uppercase tracking-wider">
            Rateizzabile
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[10.5px] font-extrabold uppercase tracking-wider">
            3 · 6 · 12 rate
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 text-[10.5px] font-extrabold uppercase tracking-wider">
            Senza interessi
          </span>
        </div>
        <p className="text-[13.5px] text-slate-600">
          Con <strong>Klarna</strong> puoi rateizzare il pagamento in <strong>3, 6 o 12 rate senza interessi</strong>.
          L&apos;idoneità viene verificata al checkout secondo i criteri di Klarna.
        </p>
      </div>
    );
  }
  if (method === "bonifico") {
    return (
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
        <div className="flex items-center gap-2 text-[#0a1f55] font-semibold text-sm mb-3">
          <Building2 className="h-4 w-4" /> Coordinate bancarie ACDT
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <Row k="Intestatario" v={BANK.holder} />
          <Row k="Banca" v={BANK.bank} />
          <div className="sm:col-span-2">
            <Row k="IBAN" v={BANK.iban} />
          </div>
          <Row k="Paese" v={BANK.paese} />
          <Row k="CIN/EU" v={BANK.cinEu} />
          <Row k="CIN/IT" v={BANK.cinIt} />
          <Row k="ABI" v={BANK.abi} />
          <Row k="CAB" v={BANK.cab} />
          <Row k="Numero C/C" v={BANK.cc} />
          <div className="sm:col-span-2">
            <Row k="BIC / SWIFT" v={BANK.bic} />
          </div>
          <div className="sm:col-span-2">
            <Row k="Causale" v={causale || "Tesseramento ACDT - Nome e Cognome"} />
          </div>
        </div>
        <p className="mt-3 text-[12px] text-slate-500">
          Effettua il bonifico utilizzando esattamente la causale indicata. Riceverai per email lo statino di pagamento di conferma.
        </p>
      </div>
    );
  }
  return null;
}

function Row({ k, v }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-[#0a1f55] font-semibold break-all">{v}</div>
    </div>
  );
}

function Brand({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-md bg-white border border-slate-200 text-[11px] font-bold text-slate-700">
      {children}
    </span>
  );
}
function PayPalPill() {
  return (
    <span className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md bg-white border border-slate-200 text-[12px] font-extrabold">
      <span className="text-[#003087]">Pay</span>
      <span className="text-[#009cde]">Pal</span>
    </span>
  );
}
function KlarnaPill() {
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-md bg-[#FFA8CD] text-[#17120F] text-[11px] font-extrabold tracking-wide">
      Klarna
    </span>
  );
}
